import { useState, useEffect, useCallback } from 'react'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { usePOSOrders, useUpdateOrderItemStatus } from '@/lib/queries/usePOS'
import type { POSOrder, POSOrderItem, POSItemStatus } from '@/lib/types/pos'

// ── Status config ──────────────────────────────────────────────────────────────

const ITEM_STATUS_NEXT: Partial<Record<POSItemStatus, POSItemStatus>> = {
  sent:        'in_progress',
  in_progress: 'ready',
  ready:       'served',
}

const ITEM_STATUS_LABEL: Record<POSItemStatus, string> = {
  pending:     'Pending',
  sent:        'New',
  in_progress: 'Cooking',
  ready:       'Ready',
  served:      'Served',
  voided:      'Voided',
}

const ITEM_STATUS_STYLE: Record<POSItemStatus, string> = {
  pending:     'border-zinc-700 bg-zinc-800/50 text-zinc-500',
  sent:        'border-amber-500/60 bg-amber-500/10 text-amber-300',
  in_progress: 'border-blue-500/60 bg-blue-500/10 text-blue-300',
  ready:       'border-green-500/60 bg-green-500/10 text-green-300',
  served:      'border-zinc-700 bg-zinc-800/30 text-zinc-600',
  voided:      'border-zinc-800 bg-zinc-900 text-zinc-700 line-through',
}

/** Colour the ticket header border by how long the order has been open */
function ticketAgeStyle(openedAt: string): string {
  const mins = (Date.now() - parseISO(openedAt).getTime()) / 60_000
  if (mins < 10) return 'border-green-500/40'
  if (mins < 20) return 'border-amber-500/60'
  return 'border-red-500/70'
}

function ticketAgeBadgeStyle(openedAt: string): string {
  const mins = (Date.now() - parseISO(openedAt).getTime()) / 60_000
  if (mins < 10) return 'bg-green-500/15 text-green-400'
  if (mins < 20) return 'bg-amber-500/15 text-amber-400'
  return 'bg-red-500/15 text-red-400'
}

// ── Order ticket card ──────────────────────────────────────────────────────────

function OrderTicket({
  order,
  onItemClick,
  updating,
}: {
  order:       POSOrder
  onItemClick: (item: POSOrderItem) => void
  updating:    Set<string>
}) {
  const kitchenItems = (order.orderItems ?? []).filter(
    (i) => i.status !== 'voided' && i.status !== 'served' && i.status !== 'pending',
  )
  const allReady = kitchenItems.length > 0 && kitchenItems.every((i) => i.status === 'ready')

  return (
    <div className={`flex flex-col rounded-xl border-2 bg-zinc-900 overflow-hidden ${ticketAgeStyle(order.openedAt)}`}>
      {/* Ticket header */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/60 border-b border-zinc-700/50">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-white tracking-tight">
            {order.table?.name ?? '—'}
          </span>
          {order.covers && (
            <span className="text-xs text-zinc-400">{order.covers} covers</span>
          )}
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ticketAgeBadgeStyle(order.openedAt)}`}>
          {formatDistanceToNow(parseISO(order.openedAt), { addSuffix: false })}
        </span>
      </div>

      {/* Items */}
      <div className="flex-1 p-3 space-y-1.5">
        {kitchenItems.length === 0 ? (
          <p className="text-xs text-zinc-600 text-center py-4">No items</p>
        ) : (
          kitchenItems.map((item) => {
            const next = ITEM_STATUS_NEXT[item.status as POSItemStatus]
            const isUpdating = updating.has(item.documentId)
            return (
              <button
                key={item.documentId}
                disabled={!next || isUpdating}
                onClick={() => next && onItemClick(item)}
                className={[
                  'w-full text-left rounded-lg border px-3 py-2.5 transition-all',
                  ITEM_STATUS_STYLE[item.status as POSItemStatus] ?? ITEM_STATUS_STYLE.sent,
                  next && !isUpdating ? 'hover:brightness-125 active:scale-[0.98] cursor-pointer' : 'cursor-default',
                  isUpdating ? 'opacity-50' : '',
                ].join(' ')}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-sm leading-tight">
                      {item.weightGrams != null ? (
                        <span className="text-white/60 mr-1">
                          {item.weightGrams % 1000 === 0 ? `${item.weightGrams / 1000} kg` : `${item.weightGrams} g`}
                        </span>
                      ) : item.quantity > 1 && (
                        <span className="text-white/60 mr-1">{item.quantity}×</span>
                      )}
                      {item.menuItem?.name}
                    </span>
                    {item.variant && (
                      <p className="text-xs opacity-70 mt-0.5">{item.variant.name}</p>
                    )}
                    {item.notes && (
                      <p className="text-xs text-yellow-400/80 mt-0.5 italic">📝 {item.notes}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                      {ITEM_STATUS_LABEL[item.status as POSItemStatus]}
                    </span>
                    {next && (
                      <span className="text-[10px] opacity-50">
                        tap → {ITEM_STATUS_LABEL[next]}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            )
          })
        )}
      </div>

      {/* All ready banner */}
      {allReady && (
        <div className="shrink-0 px-4 py-2.5 bg-green-500/10 border-t border-green-500/30 text-center">
          <span className="text-sm font-bold text-green-400">✓ All ready — notify server</span>
        </div>
      )}
    </div>
  )
}

// ── Kitchen page ───────────────────────────────────────────────────────────────

export default function KitchenPage() {
  const today = format(new Date(), 'yyyy-MM-dd')
  const [now, setNow] = useState(new Date())
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  // Poll every 15 s
  const { data: ordersResp, dataUpdatedAt } = usePOSOrders(today, { refetchInterval: 15_000 })
  const allOrders: POSOrder[] = (ordersResp as any)?.data ?? []

  // Only show orders with at least one kitchen-visible item
  const activeOrders = allOrders.filter((o) => {
    if (o.status === 'voided' || o.status === 'paid') return false
    return (o.orderItems ?? []).some(
      (i) => i.status === 'sent' || i.status === 'in_progress' || i.status === 'ready',
    )
  })

  const updateStatus = useUpdateOrderItemStatus()

  const handleItemClick = useCallback((item: POSOrderItem) => {
    const next = ITEM_STATUS_NEXT[item.status as POSItemStatus]
    if (!next) return
    setUpdatingIds((prev) => new Set(prev).add(item.documentId))
    updateStatus.mutate(
      { documentId: item.documentId, status: next },
      { onSettled: () => setUpdatingIds((prev) => { const s = new Set(prev); s.delete(item.documentId); return s }) },
    )
  }, [updateStatus])

  const lastUpdate = dataUpdatedAt ? format(new Date(dataUpdatedAt), 'HH:mm:ss') : '—'

  return (
    <div className="min-h-dvh bg-zinc-950 text-white flex flex-col">

      {/* Top bar */}
      <div className="shrink-0 flex items-center justify-between px-6 py-3 bg-zinc-900 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold tracking-tight">Kitchen Display</span>
          <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full">
            {activeOrders.length} active order{activeOrders.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm text-zinc-400">
          <span className="text-xs">Updated {lastUpdate}</span>
          <span className="font-mono text-base text-white font-bold tabular-nums">
            {format(now, 'HH:mm:ss')}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="shrink-0 flex items-center gap-4 px-6 py-2 border-b border-zinc-800/50 bg-zinc-900/50">
        {(['sent', 'in_progress', 'ready'] as POSItemStatus[]).map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm border ${ITEM_STATUS_STYLE[s]}`} />
            <span className="text-xs text-zinc-400">{ITEM_STATUS_LABEL[s]}</span>
          </div>
        ))}
        <span className="text-xs text-zinc-600 ml-2">Tap item to advance status</span>
      </div>

      {/* Order grid */}
      <div className="flex-1 p-4 overflow-y-auto">
        {activeOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <div className="text-6xl">🍽️</div>
            <p className="text-xl font-semibold text-zinc-400">No orders in kitchen</p>
            <p className="text-sm text-zinc-600">Orders appear here when sent from the POS terminal</p>
          </div>
        ) : (
          <div className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
          >
            {activeOrders.map((order) => (
              <OrderTicket
                key={order.documentId}
                order={order}
                onItemClick={handleItemClick}
                updating={updatingIds}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
